import { API_URL } from "@/utils/constant";

/**
 * DEFAULT for the envelope `source` field — the per-build platform constant,
 * configured in one place via `@/utils/constant` (env `VITE_ANALYTICS_SOURCE`,
 * default "vitrin"). The backend uses it to segment storefront vs in-miniapp
 * activity (see the integration guide §`source` vs `mini_app_uuid`).
 *
 * A launch carrying `?source=…` overrides it for that launch only — see
 * `./source`. Read `getSource()`, never this constant, when building an event.
 */
export { ANALYTICS_SOURCE } from "@/utils/constant";

/** Single ingest endpoint for every tracked event. */
export const TRACK_ENDPOINT = `${API_URL}/track`;

/**
 * The marketing analytics feature can be turned off without a deploy via
 * `VITE_MARKETING_ANALYTICS_ENABLED=false`. Defaults to on. The client also
 * self-disables at runtime if the endpoint answers `404` (server flag off).
 */
export const ANALYTICS_ENABLED =
  (import.meta.env.VITE_MARKETING_ANALYTICS_ENABLED ?? "true") !== "false";

/** Flush the batch when it reaches this many events… */
export const BATCH_MAX_EVENTS = 10;
/** …or after this long since the first queued event, whichever comes first. */
export const BATCH_FLUSH_MS = 5_000;
/** Hard cap the server accepts per request (guide: "~50 events per batch"). */
export const BATCH_HARD_LIMIT = 50;

/** localStorage / sessionStorage keys owned by the analytics layer. */
export const STORAGE_KEYS = {
  anonymousId: "amz_anonymous_id",
  utmFirst: "amz_utm_first",
  utmActive: "amz_utm_active",
  session: "amz_session",
  source: "amz_source",
  elementViews: "amz_element_views",
} as const;

/**
 * Click / view instrumentation (`element_clicked`, `element_viewed`) can be
 * turned off independently of the rest of analytics via
 * `VITE_ELEMENT_TRACKING_ENABLED=false` — the funnel and content events keep
 * flowing. Defaults to on; still gated by {@link ANALYTICS_ENABLED}.
 */
export const ELEMENT_TRACKING_ENABLED =
  (import.meta.env.VITE_ELEMENT_TRACKING_ENABLED ?? "true") !== "false";

/**
 * `element_viewed` alone — the highest-volume event in the system, and the CTR
 * denominator. `VITE_ELEMENT_VIEW_TRACKING_ENABLED=false` drops the impressions
 * while keeping the clicks: you lose CTR, you keep the click counts. This is the
 * knob to reach for if ingest volume ever becomes the problem.
 */
export const ELEMENT_VIEW_TRACKING_ENABLED =
  (import.meta.env.VITE_ELEMENT_VIEW_TRACKING_ENABLED ?? "true") !== "false";

/** An element counts as "seen" at this much visibility… */
export const ELEMENT_VIEW_RATIO = 0.5;
/** …held for this long. Under either bar it scrolled past — it was not seen. */
export const ELEMENT_VIEW_DWELL_MS = 1_000;
/**
 * Safety net on the per-session "seen" set. A real session never approaches it;
 * a runaway list (an infinite feed, a render loop) stops emitting views here
 * rather than flooding `/track`.
 */
export const ELEMENT_VIEW_SESSION_LIMIT = 500;
